import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone, RefreshCw } from "lucide-react";
import { authHeaders } from "../pages/AdminPage";
import { inr } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badge = {
  pending: "bg-[#C5A059]/20 text-[#7a5a20]",
  payment_pending: "bg-[#C5A059]/20 text-[#7a5a20]",
  payment_failed: "bg-red-800/10 text-red-800",
  confirmed: "bg-[#2E6669]/15 text-[#2E6669]",
  delivered: "bg-green-800/10 text-green-800",
  cancelled: "bg-red-800/10 text-red-800",
};

export const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: authHeaders() });
      setOrders(res.data);
    } catch {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, { headers: authHeaders() });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Order #${id} marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  if (loading)
    return (
      <p data-testid="orders-loading" className="py-20 text-center font-serif-d italic text-xl text-[#8A818F]">
        Loading orders…
      </p>
    );

  if (!orders.length)
    return (
      <p data-testid="orders-empty" className="py-20 text-center font-serif-d italic text-xl text-[#8A818F]">
        No orders yet — they'll appear here the moment one is placed.
      </p>
    );

  return (
    <div className="space-y-6" data-testid="orders-list">
      <div className="flex justify-between items-center">
        <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">
          {orders.length} order{orders.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={load}
          data-testid="orders-refresh-button"
          className="flex items-center gap-2 font-accent text-[10px] tracking-[0.25em] uppercase text-[#4A2B58] hover:text-[#C5A059] transition-colors"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      {orders.map((o) => {
        const phoneDigits = (o.phone || "").replace(/\D/g, "").replace(/^91/, "");
        const waText = encodeURIComponent(
          `Hello ${o.customer_name}, your Innus Lace Centre order #${o.id} (${inr(o.total)}) is confirmed and will be delivered soon. Thank you!`
        );
        return (
          <article
            key={o.id}
            data-testid={`order-card-${o.id}`}
            className="bg-[#FBF9F6] border border-[#E8E4DF] p-6 md:p-8"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-[#E8E4DF]">
              <div className="flex items-center gap-4">
                <h3 className="font-serif-d text-2xl text-[#2D1B36]">#{o.id}</h3>
                <span
                  data-testid={`order-status-${o.id}`}
                  className={`font-accent text-[10px] tracking-[0.2em] uppercase px-3 py-1 ${badge[o.status] || badge.pending}`}
                >
                  {o.status}
                </span>
              </div>
              <span className="text-xs text-[#8A818F]">
                {o.created_at ? new Date(o.created_at).toLocaleString("en-IN") : ""}
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-6 py-5">
              <div>
                <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F] mb-2">Customer</p>
                <p className="font-medium text-[#2D1B36]">{o.customer_name}</p>
                <a
                  href={`tel:+91${phoneDigits}`}
                  data-testid={`order-phone-${o.id}`}
                  className="inline-flex items-center gap-1.5 text-sm text-[#2E6669] hover:text-[#C5A059] transition-colors mt-1"
                >
                  <Phone size={12} /> +91 {phoneDigits}
                </a>
                <p className="text-sm text-[#514357] mt-2 leading-relaxed">{o.address}</p>
                {o.note && <p className="text-xs text-[#8A818F] mt-1 italic">Note: {o.note}</p>}
              </div>
              <div>
                <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F] mb-2">Items</p>
                <ul className="space-y-1">
                  {(o.items || []).map((i, idx) => (
                    <li key={idx} className="text-sm text-[#514357] flex justify-between gap-4">
                      <span>
                        {i.qty} × {i.name}
                      </span>
                      <span className="text-[#2D1B36]">{inr(i.price * i.qty)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 pt-3 border-t border-[#E8E4DF] flex justify-between font-medium text-[#2D1B36]">
                  <span className="font-accent text-[10px] tracking-[0.25em] uppercase">Total · {o.payment || "Online"}</span>
                  <span data-testid={`order-total-${o.id}`} className="font-serif-d text-xl">{inr(o.total)}</span>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-5 border-t border-[#E8E4DF]">
              {["pending", "confirmed", "delivered", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(o.id, s)}
                  disabled={o.status === s}
                  data-testid={`order-mark-${s}-${o.id}`}
                  className={`font-accent text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${
                    o.status === s
                      ? "bg-[#4A2B58] text-[#FBF9F6] border-[#4A2B58] cursor-default"
                      : "border-[#C5A059]/50 text-[#4A2B58] hover:bg-[#C5A059]/10"
                  }`}
                >
                  {s}
                </button>
              ))}
              <a
                href={`https://wa.me/91${phoneDigits}?text=${waText}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`order-whatsapp-${o.id}`}
                className="inline-flex items-center gap-2 font-accent text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-[#2E6669] text-[#FBF9F6] hover:bg-[#1E4749] transition-colors duration-300 ml-auto"
              >
                <MessageCircle size={13} /> WhatsApp Customer
              </a>
            </div>
          </article>
        );
      })}
    </div>
  );
};
